import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { FieldRow } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { normalizeEmployeeLogin } from "@/lib/auth";
import { getHeroPlanningEmployees } from "@/lib/projects";
import {
  updateAssignmentFeinplanungEditors,
  type PlanungFeinplanungEditor,
} from "@/lib/montageplanung";

interface EmployeeOption {
  login: string;
  name: string;
}

export function FinePlanningDelegatesSection({
  assignmentId,
  initialEditors,
  onChanged,
  compact,
}: {
  assignmentId: number;
  initialEditors: PlanungFeinplanungEditor[];
  onChanged?: () => void;
  /** Im Modal ohne eigenes Feld-Label. */
  compact?: boolean;
}) {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialLogins = useMemo(
    () =>
      new Set(
        initialEditors
          .map((e) => normalizeEmployeeLogin(e.employee_login))
          .filter(Boolean),
      ),
    [initialEditors],
  );

  useEffect(() => {
    setSelected(new Set(initialLogins));
  }, [initialLogins, assignmentId]);

  useEffect(() => {
    setLoadingEmployees(true);
    void getHeroPlanningEmployees()
      .then((list) => {
        const opts = (list ?? [])
          .map((p) => {
            const login = normalizeEmployeeLogin(String(p.email || ""));
            const name = String(p.name || p.email || "").trim() || login;
            return login ? { login, name } : null;
          })
          .filter((row): row is EmployeeOption => row != null)
          .sort((a, b) => a.name.localeCompare(b.name, "de"));
        setEmployees(opts);
      })
      .catch(() => setEmployees([]))
      .finally(() => setLoadingEmployees(false));
  }, []);

  async function persist(next: Set<string>) {
    setSaving(true);
    setError(null);
    try {
      await updateAssignmentFeinplanungEditors(assignmentId, [...next]);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Freigabe konnte nicht gespeichert werden.");
      setSelected(new Set(initialLogins));
    } finally {
      setSaving(false);
    }
  }

  function toggle(login: string) {
    const canon = normalizeEmployeeLogin(login);
    if (!canon || saving) return;
    const next = new Set(selected);
    if (next.has(canon)) next.delete(canon);
    else next.add(canon);
    setSelected(next);
    void persist(next);
  }

  const body = (
    <>
      {error && <Alert variant="error" className="mb-2">{error}</Alert>}
      {loadingEmployees ? (
        <div className="flex items-center gap-2 py-2 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          Mitarbeiter werden geladen …
        </div>
      ) : employees.length === 0 ? (
        <p className="text-sm text-label-tertiary">Keine Mitarbeiter verfügbar.</p>
      ) : (
        <ul className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-separator-opaque p-2">
          {employees.map((emp) => {
            const checked = selected.has(emp.login);
            return (
              <li key={emp.login}>
                <label
                  className={
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-fill-tertiary " +
                    (saving ? "pointer-events-none opacity-60" : "")
                  }
                >
                  <input
                    type="checkbox"
                    className="shrink-0"
                    checked={checked}
                    disabled={saving}
                    onChange={() => toggle(emp.login)}
                  />
                  <span className="min-w-0 truncate text-label">{emp.name}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      {selected.size > 0 && (
        <p className="mt-1.5 text-xs text-label-tertiary">
          {selected.size} Mitarbeiter mit Bearbeitungsrecht für die Feinplanung.
        </p>
      )}
    </>
  );

  if (compact) {
    return (
      <div>
        <p className="mb-3 text-sm text-label-secondary">
          Ausgewählte Mitarbeiter können nur die Feinplanung bearbeiten, nicht den Einsatz selbst.
        </p>
        {body}
      </div>
    );
  }

  return (
    <FieldRow
      label="Feinplanung freigeben"
      hint="Ausgewählte Mitarbeiter können nur die Feinplanung dieses Einsatzes bearbeiten, nicht den Einsatz selbst."
    >
      {body}
    </FieldRow>
  );
}
